import React, { useCallback, useEffect } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Alert,
  Button,
  Checkbox,
  Popconfirm,
  Tooltip,
} from '@surecompanies/core_components'
import RHFInput from '../../PlanBenefitOverrides/components/form/RHFInput'
import RHFSelect from '../../PlanBenefitOverrides/components/form/RHFSelect'
import { useDebounce } from 'hooks/useDebounce'
import OverrideAuditStrip from '../../../components/OverrideAuditStrip'
import {
  PlanServiceAreaOverrideFormSchema,
  apiPayloadToForm,
  flatToApiPayload,
  type ServiceAreaViewMetadata,
  type PlanServiceAreaOverrideFormValues,
} from './planServiceAreaOverrideForm.schema'
import { US_STATE_OPTIONS } from '../../../constants'
import {
  useCheckDuplicateServiceAreaOverrideRule,
  SERVICE_AREA_OVERRIDE_CHECK_DUPLICATE_QUERY_KEY,
} from '../hooks/useCheckDuplicateServiceAreaOverrideRule'
import { useDeleteServiceAreaOverrideRule } from '../hooks/useDeleteServiceAreaOverrideRule'
import { useQueryClient } from '@tanstack/react-query'
import type { CreatePlanServiceAreaOverrideRuleRequestDto } from '../../../api/dto/plan-service-area-override-rules.dto'
import { relativeTimeString } from 'enrollment-platform/utils/relative-time-string'
import styles from '../../../components/PlanOverridesComponents.module.scss'

const DUPLICATE_CHECK_DEBOUNCE_MS = 400
const CURRENT_YEAR = new Date().getFullYear()

export type ServiceAreaFormMode = 'create' | 'edit' | 'view'

export interface ServiceAreaServerFieldError {
  field: string
  errors: string[]
}

interface Props {
  mode?: ServiceAreaFormMode
  defaultValues?: Partial<PlanServiceAreaOverrideFormValues>
  serverErrors?: ServiceAreaServerFieldError[]
  metadata?: ServiceAreaViewMetadata
  onSubmit?: (
    data: CreatePlanServiceAreaOverrideRuleRequestDto
  ) => Promise<void>
  onCancel?: () => void
  isSubmitting?: boolean
  onDuplicateFound?: (ruleId: string) => void
  extraContent?: React.ReactNode
  step?: number
}

export default function PlanServiceAreaOverrideForm({
  mode = 'create',
  defaultValues,
  serverErrors,
  metadata,
  onSubmit,
  onCancel,
  isSubmitting = false,
  onDuplicateFound,
  extraContent,
  step,
}: Props) {
  const isEditMode = mode === 'edit'
  const isReadOnly = mode === 'view'
  const isCreateMode = mode === 'create'

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, submitCount },
  } = useForm<PlanServiceAreaOverrideFormValues>({
    resolver: zodResolver(PlanServiceAreaOverrideFormSchema),
    defaultValues: {
      year: CURRENT_YEAR,
      state: '',
      issuerId: '',
      serviceAreaId: '',
      hiosPlanId: '',
      countyCode: '',
      zipcode: '',
      sourceName: '',
      serviceAreaName: '',
      coverEntireState: false,
      partialCounty: false,
      isActive: false,
      ...defaultValues,
    },
  })

  useEffect(() => {
    if (!serverErrors || serverErrors.length === 0) return
    serverErrors.forEach(({ field, errors: fieldErrors }) => {
      if (!field || fieldErrors.length === 0) return
      const mappedField = field.startsWith('match.')
        ? (field.slice(
            'match.'.length
          ) as keyof PlanServiceAreaOverrideFormValues)
        : field.startsWith('params.')
          ? (field.slice(
              'params.'.length
            ) as keyof PlanServiceAreaOverrideFormValues)
          : (field as keyof PlanServiceAreaOverrideFormValues)
      setError(mappedField, { type: 'server', message: fieldErrors.join(', ') })
    })
  }, [serverErrors, setError])

  const [
    year,
    state,
    issuerId,
    serviceAreaId,
    hiosPlanId,
    countyCode,
    zipcode,
    sourceName,
    serviceAreaName,
  ] = useWatch({
    control,
    name: [
      'year',
      'state',
      'issuerId',
      'serviceAreaId',
      'hiosPlanId',
      'countyCode',
      'zipcode',
      'sourceName',
      'serviceAreaName',
    ],
  })

  const duplicateKey =
    isCreateMode &&
    year &&
    state &&
    issuerId &&
    serviceAreaId &&
    hiosPlanId &&
    countyCode &&
    zipcode
      ? `${year}|${state}|${issuerId}|${serviceAreaId}|${hiosPlanId}|${countyCode}|${zipcode}`
      : null
  const debouncedDuplicateKey = useDebounce(
    duplicateKey,
    DUPLICATE_CHECK_DEBOUNCE_MS
  )
  const duplicateQuery = debouncedDuplicateKey
    ? {
        year: Number(year),
        state,
        issuerId,
        serviceAreaId,
        hiosPlanId,
        countyCode,
        zipcode,
      }
    : {}
  const { data: duplicateData } = useCheckDuplicateServiceAreaOverrideRule(
    duplicateQuery,
    Boolean(debouncedDuplicateKey)
  )
  const showDuplicateAlert = duplicateData?.exists === true
  const duplicateRule = duplicateData?.exists ? duplicateData.rule : null

  const queryClient = useQueryClient()
  const { mutate: deleteDuplicate, isPending: isDeletingDuplicate } =
    useDeleteServiceAreaOverrideRule()

  const handleDeleteDuplicate = useCallback(() => {
    if (!duplicateRule) return
    deleteDuplicate(duplicateRule.id, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [SERVICE_AREA_OVERRIDE_CHECK_DUPLICATE_QUERY_KEY],
        })
      },
    })
  }, [duplicateRule, deleteDuplicate, queryClient])

  const handleFormSubmit = async (
    values: PlanServiceAreaOverrideFormValues
  ) => {
    if (!onSubmit) return
    const payload = flatToApiPayload(values)
    await onSubmit(payload)
  }

  const formHasErrors = submitCount > 0 && Object.keys(errors).length > 0

  const identityFieldsFilled =
    Boolean(year) &&
    Boolean(state) &&
    Boolean(issuerId) &&
    Boolean(serviceAreaId) &&
    Boolean(hiosPlanId) &&
    Boolean(countyCode) &&
    Boolean(zipcode)

  const paramsFieldsFilled = Boolean(sourceName) && Boolean(serviceAreaName)

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      {isCreateMode && showDuplicateAlert && duplicateRule && (
        <div className={styles.duplicateBanner}>
          <div className={styles.duplicateBannerIcon}>⚠</div>
          <div className={styles.duplicateBannerBody}>
            <span className={styles.duplicateBannerTitle}>
              Rule already exists for this combination
            </span>
            <span className={styles.duplicateBannerValue}>
              {duplicateRule.match.state}
              <span className={styles.duplicateBannerDot}> · </span>
              Issuer {duplicateRule.match.issuerId}
              <span className={styles.duplicateBannerDot}> · </span>
              Service Area {duplicateRule.match.serviceAreaId}
              <span className={styles.duplicateBannerDot}> · </span>
              {duplicateRule.match.year}
            </span>
            <span className={styles.duplicateBannerMeta}>
              <span
                className={`${styles.statusTag} ${duplicateRule.status === 'APPROVED' ? styles.statusActive : duplicateRule.status === 'REJECTED' ? styles.statusInactive : styles.statusDraft}`}
              >
                {duplicateRule.status}
              </span>
              <span className={styles.duplicateBannerDot}>·</span>
              {relativeTimeString(duplicateRule.updatedAt)}
            </span>
          </div>
          <div className={styles.duplicateBannerActions}>
            <Button
              type="primary"
              size="small"
              onClick={() => onDuplicateFound?.(duplicateRule.id)}
              disabled={!onDuplicateFound}
            >
              Edit
            </Button>
            <Popconfirm
              title="Delete this rule?"
              description="This will permanently delete the existing rule."
              okText="Delete"
              okType="danger"
              cancelText="Cancel"
              onConfirm={handleDeleteDuplicate}
            >
              <Button
                type="default"
                size="small"
                danger
                loading={isDeletingDuplicate}
              >
                Delete
              </Button>
            </Popconfirm>
          </div>
        </div>
      )}

      {formHasErrors && (
        <Alert
          type="error"
          showIcon
          message="Please fix the errors below before saving."
          className={styles.formValidationAlert}
        />
      )}

      <div className={styles.stepList}>
        {/* Step 1 — Identity (immutable after create) */}
        {(step === undefined || step === 1) && (
          <div className={styles.step}>
            <div className={styles.stepLeft}>
              <div className={styles.stepBadge}>1</div>
              <div className={styles.stepRail} />
            </div>
            <div className={styles.stepContent}>
              <p className={styles.stepTitle}>Target</p>
              <div className={styles.stepFieldRow}>
                <RHFInput
                  name="year"
                  control={control}
                  label="Year"
                  required
                  type="number"
                  placeholder="e.g. 2025"
                  disabled={isReadOnly || isEditMode}
                />
                <RHFSelect
                  name="state"
                  control={control}
                  label="State"
                  required
                  options={US_STATE_OPTIONS}
                  placeholder="Select state"
                  showSearch
                  optionFilterProp="label"
                  disabled={isReadOnly || isEditMode}
                />
                <RHFInput
                  name="issuerId"
                  control={control}
                  label="Issuer ID"
                  required
                  maxLength={5}
                  placeholder="e.g. 83396"
                  disabled={isReadOnly || isEditMode}
                />
                <RHFInput
                  name="hiosPlanId"
                  control={control}
                  label="HIOS Plan ID"
                  required
                  placeholder="e.g. 83396OH009"
                  disabled={isReadOnly || isEditMode}
                />
                <RHFInput
                  name="serviceAreaId"
                  control={control}
                  label="Service Area ID"
                  required
                  placeholder="e.g. TXS269"
                  disabled={isReadOnly || isEditMode}
                />
                <RHFInput
                  name="countyCode"
                  control={control}
                  label="County Code"
                  required
                  placeholder="e.g. 59"
                  disabled={isReadOnly || isEditMode}
                />
                <RHFInput
                  name="zipcode"
                  control={control}
                  label="Zipcode"
                  required
                  placeholder="e.g. 43768"
                  maxLength={5}
                  padStart={5}
                  disabled={isReadOnly || isEditMode}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2 — Service Area data (always editable) */}
        {(step === undefined || step === 2) && (
          <div className={styles.step}>
            <div className={styles.stepLeft}>
              <div className={styles.stepBadge}>2</div>
            </div>
            <div className={`${styles.stepContent} ${styles.stepContentLast}`}>
              <p className={styles.stepTitle}>Override data</p>
              <div>
                <div className={styles.upsertCheckboxRow}>
                  <Controller
                    name="coverEntireState"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        checked={Boolean(field.value)}
                        onChange={(e) => field.onChange(e.target.checked)}
                        disabled={isReadOnly}
                      >
                        Cover Entire State
                      </Checkbox>
                    )}
                  />
                  <Controller
                    name="partialCounty"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        checked={Boolean(field.value)}
                        onChange={(e) => field.onChange(e.target.checked)}
                        disabled={isReadOnly}
                      >
                        Partial County
                      </Checkbox>
                    )}
                  />
                  <Controller
                    name="isActive"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        checked={Boolean(field.value)}
                        onChange={(e) => field.onChange(e.target.checked)}
                        disabled={isReadOnly}
                      >
                        Is Active
                      </Checkbox>
                    )}
                  />
                </div>
              </div>

              <div>
                <div className={styles.stepFieldRow}>
                  <RHFInput
                    name="sourceName"
                    control={control}
                    label="Source Name"
                    required
                    placeholder="e.g. IDEON"
                    disabled={isReadOnly}
                  />
                  <RHFInput
                    name="serviceAreaName"
                    control={control}
                    label="Service Area Name"
                    required
                    placeholder="e.g. COMMERCIAL (HMO)"
                    disabled={isReadOnly}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3 — Evidence documents (rendered via extraContent) */}
        {(step === undefined || step === 3) && extraContent}
      </div>

      {!isReadOnly && (
        <div className={styles.formFooter}>
          {isEditMode && metadata ? (
            <OverrideAuditStrip metadata={metadata} />
          ) : (
            <div />
          )}
          <div className={styles.formFooterActions}>
            <Button type="default" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Tooltip
              title={
                showDuplicateAlert
                  ? 'Resolve the duplicate rule above before saving.'
                  : !isEditMode && !identityFieldsFilled
                    ? 'Fill in all identity fields before saving.'
                    : !paramsFieldsFilled
                      ? 'Enter source name and service area name before saving.'
                      : undefined
              }
            >
              <Button
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                disabled={
                  isSubmitting ||
                  showDuplicateAlert ||
                  (!isEditMode && !identityFieldsFilled) ||
                  !paramsFieldsFilled
                }
              >
                Save
              </Button>
            </Tooltip>
          </div>
        </div>
      )}
    </form>
  )
}

export { apiPayloadToForm }
